import React, {Component, FormEvent, FocusEvent, ChangeEvent, DragEvent, MouseEvent} from "react";
import {FormControl, FormHelperText, OutlinedInput, InputLabel, InputAdornment, Button} from '@material-ui/core';
import {ValidatedFieldProps, ValidatedFieldState} from 'components/forms/validation';
import './index.scss';
import {PhotoIcon, UploadIcon} from 'components/Icon';

export interface ImageUploadFieldProps extends ValidatedFieldProps {
  disabled?: boolean;
  name?: string;
  label?: string;
  onInput?: (ev:FormEvent<HTMLInputElement>|DragEvent<HTMLDivElement>)=>void;
  onBlur?: (ev:FocusEvent<HTMLInputElement>)=>void;
  className?: string;
  inputRef?: (el:HTMLInputElement|null)=>void;
  variant?: 'expanded'|'collapsed';
}

export interface ImageUploadFieldState extends ValidatedFieldState {
  labelWidth: number|undefined;
  fileName:string|null;
  imageURL:string|null;
}

export default class ImageUploadField extends Component<ImageUploadFieldProps, ImageUploadFieldState> {
  private inputLabel = React.createRef<HTMLLabelElement>();
  public state = {
    dirty: false,
    touched: false,
    validationMessage: undefined,
    labelWidth: undefined,
    fileName: null,
    imageURL: null
  };

  componentDidMount(): void {
    const current: any = this.inputLabel.current;
    if(current) {
      this.setState({ labelWidth: current.offsetWidth });
    }
    if(this.props.model && this.props.name) {
      const value = this.props.model[this.props.name];
      if(typeof(value) === 'string') {
        this.setState({imageURL: value});
      } else if(value instanceof URL) {
        this.setState({imageURL: value.toString()});
      } else {
        // TODO
      }
    }
    if(this.props.validationGroup && this.props.name) {
      this.props.validationGroup.addFieldListener(this.props.name, this.onValidChanged);
    }
  }

  componentWillUnmount(): void {
    if(this.props.validationGroup && this.props.name) {
      this.props.validationGroup.removeFieldListener(this.props.name, this.onValidChanged);
    }
    if(this.state.imageURL) {
      URL.revokeObjectURL(this.state.imageURL!);
    }
  }

  onValidChanged = (errors: string[], forceDirty: boolean) => {
    const touched = this.state.touched || forceDirty;
    const dirty = this.state.dirty || forceDirty;
    if(errors) {
      let msg = this.props.validationMessages ? this.props.validationMessages[errors[0]] : null;
      if(!msg) {
        msg = errors[0];
      }
      this.setState({ validationMessage: msg, dirty: dirty, touched: touched });
    } else {
      this.setState({ validationMessage: undefined, dirty: dirty, touched: touched });
    }
  };

  onFileDragOver = (ev:DragEvent<HTMLDivElement>) => {
    ev.preventDefault();
  };

  onFileDrop = (ev:DragEvent<HTMLDivElement>) => {
    ev.preventDefault();
    const file = ev.dataTransfer && ev.dataTransfer.files ? ev.dataTransfer.files[0] : null;
    this.setFile(file);
    if(this.props.onInput) {
      this.props.onInput(ev);
    }
  };

  onChange = (ev: ChangeEvent<HTMLInputElement>) => {
    const input = ev.target as HTMLInputElement;
    const file = input.files && input.files.length > 0 ? input.files[0] : null;
    this.setFile(file);
    if(this.props.onInput) {
      this.props.onInput(ev);
    }
  };

  setFile(f: File|null) {
    const oldImageURL = this.state.imageURL;
    if(oldImageURL) {
      URL.revokeObjectURL(oldImageURL);
    }
    const imageURL = f ? URL.createObjectURL(f) : null;
    this.setState({fileName: f ? f.name : null, dirty: true, imageURL: imageURL}, () => {
      if(this.props.model && this.props.name) {
        this.props.model[this.props.name] = f;
      }
      if(this.props.validationGroup && this.props.name) {
        this.props.validationGroup.validateField(this.props.name);
      }
    });
  }

  onRemoveClick = () => {
    this.setFile(null);
  };

  onBlur = (ev: FocusEvent<HTMLInputElement>) => {
    this.setState({touched: true});
    if(this.props.onBlur) {
      this.props.onBlur(ev);
    }
  };

  onRendered = (el: HTMLElement|null) => {
    if(!this.props.inputRef) {
      return;
    }
    const input = el?.querySelector('input') || null;
    this.props.inputRef(input);
  };

  onBrowseClick = (ev: MouseEvent) => {
    ev.preventDefault();
  };

  render() {
    const showError = this.state.touched && this.state.dirty && !!this.state.validationMessage;
    const variant = this.props.variant || 'collapsed';
    const label = variant === 'collapsed' ? this.state.fileName || this.props.label : this.props.label;
    const imageURL = this.state.imageURL;
    return <FormControl className={`image-upload-field ${this.props.className}`} variant="outlined">
      { label && <InputLabel ref={this.inputLabel} shrink={false}>{ label }</InputLabel> }
      { variant === 'collapsed' && <OutlinedInput notched={false} type="file" labelWidth={this.state.labelWidth} onBlur={this.onBlur}
                     onChange={this.onChange} disabled={this.props.disabled} inputProps={{accept: ".png,.jpg,.gif"}}
                     startAdornment={<InputAdornment position="start"><UploadIcon /></InputAdornment>}
                     onDragOver={this.onFileDragOver} onDrop={this.onFileDrop} /> }
      { variant === 'expanded' && <div className="expanded-field" onDragOver={this.onFileDragOver} onDrop={this.onFileDrop}>
        <div className="upload-area">
          <input type="file" onChange={this.onChange} disabled={this.props.disabled} accept=".png,.jpg,.gif" />
          { !imageURL && <>
            <PhotoIcon />
            <span className="instructions">Drop your image here, or <a href="." onClick={this.onBrowseClick}>browse</a></span>
            <span className="file-types">Supports JPG, JPEG, PNG up to 2mb</span>
          </> }
          { imageURL && <img src={imageURL} alt="" className="preview" /> }
        </div>
        { imageURL && <Button className="remove" onClick={this.onRemoveClick}>Remove</Button> }
      </div> }
      { showError && <FormHelperText className="Mui-error">{this.state.validationMessage}</FormHelperText> }
    </FormControl>;
  }
}